import React from "react";
import {
    useJWT,
} from './JWT';
import { RefreshButton } from './RefreshButton';
import { CreateButton } from './CreateButton';
import { DeleteButton } from './DeleteButton';
import { HelpoverInputGroup, HelpoverText } from './Helpover';
import {
    useParams
} from "react-router-dom";
import {
    BiLoaderCircle
} from "react-icons/bi";
import Alert from 'react-bootstrap/Alert';
import Table from 'react-bootstrap/Table';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import ButtonToolbar from 'react-bootstrap/ButtonToolbar';
import Modal from 'react-bootstrap/Modal';
import InputGroup from 'react-bootstrap/InputGroup';

function AddSecret(props) {
    let jwt = useJWT();
    const [error, setError] = React.useState("");
    const [working, setWorking] = React.useState(false);
    const [name, setName] = React.useState("");
    const [value, setValue] = React.useState("");

    async function handleAdd(jwt, event) {
        event.preventDefault();

        setWorking(true);
        fetch(`https://api.${process.env.REACT_APP_DOMAIN}/rest/bots/${props.namespace}/${props.handle}/secrets`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + jwt.token
            },
            body: JSON.stringify({
                'name': name,
                'value': value
            })
        }).then(response => {
            if (!response.ok) {
                throw new Error(`${response.status} ${response.statusText}`);
            }
            return response
        }).then(payload => {
            setError("");
            setWorking(false);
            props.onComplete();
        }).catch(error => {
            console.error("adding secret: ", error);
            setError(error);
            setWorking(false);
        });
    }

    return (
        <>
          {error &&
             (<Alert variant="danger">
                {error.message}
              </Alert>)}
          <Alert variant="info">
            Secrets can only be added and removed. You won't be able to retrieve the (un-redacted) value or edit the value through this app!
          </Alert>
          <Form onSubmit={(event) => handleAdd(jwt, event)}>
            <Form.Row>
              <InputGroup className="mb-3">
                <InputGroup.Prepend>
                  <InputGroup.Text>/secrets/</InputGroup.Text>
                </InputGroup.Prepend>
                <Form.Control placeholder="name" onChange={(e) => setName(e.target.value)} />
                <HelpoverInputGroup topic="secret name"/>
              </InputGroup>
            </Form.Row>
            <Form.Row>
              <InputGroup className="mb-3">
                <Form.Control as="textarea" placeholder="value" onChange={(e) => setValue(e.target.value)} />
                <HelpoverInputGroup topic="secret value"/>
              </InputGroup>
            </Form.Row>
            <Form.Row>
              <Button variant="warning" block size="sm" type="submit" disabled={working}>
                add secret
              </Button>
            </Form.Row>
          </Form>
        </>
    );
}

function RemoveSecret(props) {
    let jwt = useJWT();
    const [error, setError] = React.useState("");
    const [working, setWorking] = React.useState(false);

    async function handleRemove(jwt, event) {
        event.preventDefault();

        setWorking(true);
        fetch(`https://api.${process.env.REACT_APP_DOMAIN}/rest/bots/${props.namespace}/${props.handle}/secrets/${props.name}`, {
            method: 'DELETE',
            headers: {
                'Authorization': 'Bearer ' + jwt.token
            }
        }).then(response => {
            if (!response.ok) {
                throw new Error(`${response.status} ${response.statusText}`);
            }
            return response
        }).then(response => {
            setError("");
            setWorking(false);
            props.onComplete();
        }).catch(error => {
            console.error("removing secret: ", error);
            setError(error);
            setWorking(false);
        });
    }

    return (
        <>
          {error &&
             (<Alert variant="danger">
                {error.message}
              </Alert>)}
          <Alert variant="warning">
            Are you super-duper sure you want to remove <span className="text-monospace">/secrets/{props.name}</span> from <span className="font-weight-bold">{props.namespace}/{props.handle}</span>? Once it's gone, it's gone!
          </Alert>
          <Form onSubmit={(event) => handleRemove(jwt, event)}>
            <Form.Row>
              <Button variant="danger" block size="sm" type="submit" disabled={working}>
                remove secret
              </Button>
            </Form.Row>
          </Form>
        </>
    );
}

export function Secrets() {
    let jwt = useJWT();
    let { namespace, handle } = useParams();
    const [error, setError] = React.useState("");
    const [loading, setLoading] = React.useState(true);
    const [secrets, setSecrets] = React.useState([]);
    const [adding, setAdding] = React.useState(false);
    const [removing, setRemoving] = React.useState("");

    async function loadSecrets(jwt, namespace, handle) {
        fetch(`https://api.${process.env.REACT_APP_DOMAIN}/rest/bots/${namespace}/${handle}/secrets`, {
            method: 'GET',
            headers: {
                'Authorization': 'Bearer ' + jwt.token
            }
        }).then(response => {
            if (!response.ok) {
                throw new Error(`${response.status} ${response.statusText}`);
            }
            return response.json()
        }).then(payload => {
            setSecrets(payload.secrets);
            setError(false);
            setLoading(false);
        }).catch(error => {
            console.error("loading secrets: ", error);
            setSecrets([]);
            setError(error);
            setLoading(false);
        })
    }

    React.useEffect(() => {
        loadSecrets(jwt, namespace, handle);
    }, [jwt, namespace, handle]);

    if (loading) {
        return (
            <><BiLoaderCircle/></>
        );
    }
    if (error) {
        return (
            <Alert variant="danger">
              {error.message}
            </Alert>
        );
    }

    return (
        <>
          <Modal size="lg" show={adding} onHide={() => setAdding(false)}>
            <Modal.Header closeButton>
              <Modal.Title>add secret to <span className="font-weight-bold">{namespace}/{handle}</span></Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <AddSecret namespace={namespace} handle={handle} onComplete={() => {setAdding(false); loadSecrets(jwt, namespace, handle)}}/>
            </Modal.Body>
          </Modal>

          <Modal size="lg" show={removing !== ""} onHide={() => setRemoving("")}>
            <Modal.Header closeButton>
            </Modal.Header>
            <Modal.Body>
              <RemoveSecret namespace={namespace} handle={handle} name={removing} onComplete={() => {setRemoving(""); loadSecrets(jwt, namespace, handle)}}/>
            </Modal.Body>
          </Modal>

          <Alert variant="info">
            These secrets are available to <span className="font-weight-bold">{namespace}/{handle}</span> as files under <span className="text-monospace">/secrets</span>, so you don't need credentials embedded directly in your code.
          </Alert>

          <ButtonToolbar className="justify-content-end mb-1">
            <RefreshButton className="mr-1" onClick={() => loadSecrets(jwt, namespace, handle)}/>
            <CreateButton onClick={() => setAdding(true)}>add secret</CreateButton>
          </ButtonToolbar>

          <Table hover size="sm">
          <thead>
            <tr>
              <th><HelpoverText topic="secret name">name</HelpoverText></th>
              <th><HelpoverText topic="secret value">value (redacted)</HelpoverText></th>
              <th></th>
            </tr>
          </thead>

          <tbody>
          {secrets.map(secret => (
            <tr key="{secret.name}">
              <td>
                <span className="text-monospace">
                  /secrets/{secret.name}
                </span>
              </td>
              <td>
                <span className="text-monospace">
                  ███{secret.redacted_value}
                </span>
              </td>
              <td>
                <ButtonToolbar className="justify-content-end">
                  <DeleteButton onClick={() => setRemoving(secret.name)}>remove secret</DeleteButton>
                </ButtonToolbar>
              </td>
            </tr>
          ))}
          </tbody>
          </Table>
        </>
    );
}

export default Secrets;
